import { real_id } from 'src/helpers/interface_helpers';
import { get } from 'svelte/store';
import {
	computeCameraState,
	isCameraFirmwareUpdateRequired,
} from 'src/components/Camera/helpers/camera-state-helpers';
import {
	getCameraComponentKey,
	isCameraPrivacyEnabled,
} from 'src/components/Camera/helpers/camera-helpers';
import { hasActiveWebRTCSession } from 'src/components/Camera/helpers/webrtc-session-manager';
import type { CameraStreamer } from 'src/components/Camera/types/camera-status';
import type {
	DashCameraLayout,
	DashCameraWidgetConfig,
	DashCameraWidgetFeedMode,
	DashCameraWidgetVariant,
} from './dash-types';
import type { Device } from 'src/types/Device';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import { offlineStateType } from 'src/stores/device-runtime/states';
import type { DashEnergyHeatmapVariant, DashEnergyPeakVariant } from './dash-energy-types';
import { user_data } from 'src/stores/account/user';

export type { DashCameraLayout } from './dash-types';

/** @deprecated Persisted variants normalized on read. */
type LegacyDashCameraWidgetVariant = DashCameraWidgetVariant | 'triple-row' | 'device-control';

const LOCKED_LAYOUTS: DashCameraLayout[] = ['single', 'pair', 'triple-focus', 'quad', 'button-card'];

export function isTrioCameraVariant(variant?: LegacyDashCameraWidgetVariant): boolean {
	return variant === 'triple-focus' || variant === 'triple-row';
}

export function isLockedCameraLayout(
	variant?: LegacyDashCameraWidgetVariant,
): variant is DashCameraLayout {
	return !!variant && (LOCKED_LAYOUTS as string[]).includes(variant);
}

/** Composite dashCamera layouts (not legacy per-camera button cards). */
export function isSpecialDashCameraLayout(variant?: LegacyDashCameraWidgetVariant): boolean {
	return !isLegacyCameraButtonCardLayout(variant);
}

/** @deprecated Legacy dashCamera button-card widgets; use dashCardDev for per-camera cards. */
export function isLegacyCameraButtonCardLayout(variant?: LegacyDashCameraWidgetVariant): boolean {
	return variant === 'button-card' || variant === 'device-control';
}

function normalizeCameraVariant(variant?: LegacyDashCameraWidgetVariant): DashCameraWidgetVariant {
	if (variant === 'triple-row') return 'triple-focus';
	if (variant === 'device-control') return 'button-card';
	return variant || 'auto';
}

/** Locked picker variants keep their layout; `auto` / `full-preview` follow camera count. */
export function resolveCameraLayout(config: DashCameraWidgetConfig = {}, count = 0): DashCameraLayout {
	const variant = normalizeCameraVariant(config.variant);
	if (variant === 'button-card') return 'button-card';
	if (isLockedCameraLayout(variant) && variant !== 'button-card') {
		return variant;
	}
	if (count <= 1) return 'single';
	if (count === 2) return 'pair';
	if (count === 3) return 'triple-focus';
	return 'quad';
}

export function getCameraLayoutSlotCount(layout: DashCameraLayout): number {
	if (layout === 'pair') return 2;
	if (layout === 'triple-focus') return 3;
	if (layout === 'quad') return 4;
	return 1;
}

export function getCameraSelectionLimit(variant?: LegacyDashCameraWidgetVariant): number {
	const normalized = normalizeCameraVariant(variant);
	if (normalized === 'pair') return 2;
	if (isTrioCameraVariant(normalized)) return 3;
	if (normalized === 'single' || normalized === 'button-card') return 1;
	return 4;
}

/** Ref key used for picker cards that share a widget type but persist distinct variants. */
export function getDashWidgetSelectListRefKey(
	widgetType?: string,
	cameraVariant?: DashCameraWidgetVariant,
	heatmapVariant?: DashEnergyHeatmapVariant,
	peakVariant?: DashEnergyPeakVariant,
): string {
	if (widgetType === 'dashCamera') {
		if (!cameraVariant || cameraVariant === 'full-preview' || cameraVariant === 'auto') {
			return 'auto';
		}
		return cameraVariant;
	}
	if (widgetType === 'dashEnergyHeatmap' && heatmapVariant) {
		return `${widgetType}-${heatmapVariant}`;
	}
	if (widgetType === 'dashEnergyPeak' && peakVariant) {
		return `${widgetType}-${peakVariant}`;
	}
	return widgetType || '';
}

function resolvePersistedFeedMode(config: DashCameraWidgetConfig): DashCameraWidgetFeedMode {
	if (config.feedMode === 'preview' || config.feedMode === 'live') {
		return config.feedMode;
	}
	// Old widgets and deprecated per-camera maps with no feedMode stay on snapshot.
	return 'preview';
}

/** Apply defaults for missing persisted fields. Migrates deprecated `cameraFeedModes`. */
export function normalizeCameraConfig(
	config?: DashCameraWidgetConfig | null,
): Required<Pick<DashCameraWidgetConfig, 'variant' | 'feedMode' | 'sideFeedMode' | 'mainAlwaysLive'>> &
	DashCameraWidgetConfig {
	// Widgets persisted before camera config existed read back as null, which a
	// default parameter value would not catch.
	const source = config ?? {};
	const variant = normalizeCameraVariant(source.variant);
	return {
		...source,
		variant,
		feedMode: resolvePersistedFeedMode(source),
		sideFeedMode: source.sideFeedMode ?? 'live',
		mainAlwaysLive: true,
		cameraFeedModes: undefined,
	};
}

export function resolveCameraFeedMode(
	_cameraId: string | number | undefined | null,
	cameraConfig?: DashCameraWidgetConfig | null,
): DashCameraWidgetFeedMode {
	return normalizeCameraConfig(cameraConfig).feedMode;
}

export function resolveSlotFeedMode(
	role: 'main' | 'side',
	cameraConfig: DashCameraWidgetConfig = {},
	_cameraId?: string | number,
	count = 0,
): DashCameraWidgetFeedMode {
	const normalized = normalizeCameraConfig(cameraConfig);
	const layout = resolveCameraLayout(normalized, count);
	if (layout === 'triple-focus' && role === 'main') {
		return 'live';
	}
	if (layout === 'triple-focus' && role === 'side') {
		// Trio sides follow the widget live default. Snapshot is a hidden
		// test-user option on feedMode; leftover sideFeedMode: 'preview' from
		// the old default must not leave side tiles as empty gray boxes.
		return normalized.feedMode === 'preview' ? 'preview' : 'live';
	}
	return normalized.feedMode;
}

/** Wizard PreviewBox image for the selected widget type / camera variant. */
export function getDashWidgetPreviewSrc(
	widgetType?: string,
	cameraVariant?: DashCameraWidgetVariant,
): string {
	if (widgetType === 'dashCamera') {
		if (isTrioCameraVariant(cameraVariant)) {
			return './images/dash/dash-preview-dashCamera-triple-focus.svg';
		}
		if (cameraVariant === 'pair') {
			return './images/dash/dash-preview-dashCamera-pair.svg';
		}
		if (cameraVariant === 'quad') {
			return './images/dash/dash-preview-dashCamera-quad.svg';
		}
		if (cameraVariant === 'single') {
			return './images/dash/dash-preview-dashCamera-single.svg';
		}
		return './images/dash/dash-preview-dashCamera.svg';
	}
	return `./images/dash/dash-preview-${widgetType || 'dashCardDev'}.svg`;
}

export function canMountLivePlayer(
	device: Device | undefined,
	status: DeviceStatusG2 | undefined,
	settings: DeviceSettingsG2 | undefined,
	isSupportAccess = get(user_data)?.temporary === true,
): boolean {
	if (!device || !status?.online || isSupportAccess) return false;
	const cameraKey = getCameraComponentKey(device);
	const privacy = isCameraPrivacyEnabled(device, status, settings);
	const streamer = (status?.[cameraKey] as { streamer?: CameraStreamer } | undefined)?.streamer;
	const streams = (status?.[cameraKey] as { streams?: number } | undefined)?.streams;
	return (
		computeCameraState(
			privacy,
			true,
			streamer,
			isCameraFirmwareUpdateRequired(device, status),
			streams,
			hasActiveWebRTCSession(device.id),
		) === null
	);
}

export type DashCameraWidgetCardRef = {
	id: string | number;
	_type?: string;
	position?: number;
};

export function getCameraDeviceFromCard(
	card: DashCameraWidgetCardRef | undefined,
	devices: Record<string, Device>,
): Device | undefined {
	if (!card?.id) return undefined;
	return devices[card.id] as Device | undefined;
}

export function getCameraDeviceStatus(
	card: DashCameraWidgetCardRef | undefined,
	devices: Record<string, Device>,
	devicesStatus: Record<string, DeviceStatusG2 | undefined>,
): DeviceStatusG2 | undefined {
	const device = getCameraDeviceFromCard(card, devices);
	return device ? devicesStatus[real_id(device.id)] : undefined;
}

export function getCameraDeviceSettings(
	card: DashCameraWidgetCardRef | undefined,
	devices: Record<string, Device>,
	devicesSettings: Record<string, DeviceSettingsG2 | undefined>,
): DeviceSettingsG2 | undefined {
	const device = getCameraDeviceFromCard(card, devices);
	return device ? devicesSettings[real_id(device.id)] : undefined;
}

export function getCameraCardName(
	card: DashCameraWidgetCardRef | undefined,
	devices: Record<string, Device>,
): string {
	const device = getCameraDeviceFromCard(card, devices);
	return device?.name || String(card?.id ?? '');
}

export function isCameraCardOffline(
	card: DashCameraWidgetCardRef | undefined,
	devices: Record<string, Device>,
	devicesStatus: Record<string, DeviceStatusG2 | undefined>,
	deviceState: { type?: number } | undefined,
): boolean {
	const device = getCameraDeviceFromCard(card, devices);
	const status = devicesStatus[real_id(device?.id)];
	const state = deviceState;
	return !device || !status?.online || state?.type === offlineStateType;
}

export function isCameraSlotUsingLiveFeed(
	card: DashCameraWidgetCardRef | undefined,
	role: 'main' | 'side',
	cameraConfig: DashCameraWidgetConfig,
	devices: Record<string, Device>,
	count = 0,
): boolean {
	const device = getCameraDeviceFromCard(card, devices);
	if (!device || !card?.id) return false;
	return resolveSlotFeedMode(role, cameraConfig, card.id, count) === 'live';
}

/**
 * Whether a single-camera dashboard device card should mount the live WebRTC feed.
 *
 * List rows and empty widget config stay on the snapshot. Live requires a saved
 * `feedMode: 'live'` — a missing or empty config must not open a WebRTC session.
 */
export function isSingleCameraLiveFeedEnabled(
	_deviceId: string | number | undefined | null,
	cameraConfig?: DashCameraWidgetConfig | null,
): boolean {
	if (!cameraConfig || Object.keys(cameraConfig).length === 0) return false;
	return cameraConfig.feedMode === 'live';
}
